/**
 * Frontend product-title formatter
 *
 * Cleans up Shopify titles for display without modifying store data.
 * Convention: Line + Colour + Size/Detail, Title Case.
 */

/** Known product-line prefixes → clean display name (longest match first) */
const LINE_MAP: [string, string][] = [
  // Longest first for correct matching
  ["nott denim baguette", "Denim Baguette"],
  ["nott denim basket", "Denim Basket"],
  ["nott denim / corduroy", "Denim Corduroy"],
  ["nott denim / pied de poule", "Denim Pied de Poule"],
  ["nott bag crossbody strap", "Crossbody Strap"],
  ["nott chunky short cord", "Chunky Short Cord"],
  ["nott cross cord", "Cross Cord"],
  ["nott chain spark", "Chain Spark"],
  ["nott chain", "Chain"],
  ["nott baguette", "Baguette"],
  ["nott cloud", "Cloud"],
  ["nott denim", "Denim"],
  ["nott organza", "Organza"],
  ["nott tulle", "Organza"],
  ["nott v stripe", "V Stripe"],
  ["nott v", "V"],
  ["metallic nottie", "Metallic NOTTie"],
  ["beachy nott", "Beachy NOTT"],
  ["bigchain", "BigChain"],
  ["bignott", "BigNOTT"],
  ["nottie", "NOTTie"],
  ["create your own", "Create Your Own"],
  // "Laura Maree X NOTT" collabs — keep as-is
];

/** Brand tokens whose casing should be preserved */
const PRESERVE = new Set([
  "NOTT", "NOTTie", "BigNOTT", "BigChain", "DENIM", "BEACHY",
  "X", "×", "USB", "XL", "DIY",
]);

/** Size tokens that get a dash prefix */
const SIZE_TOKENS: Record<string, string> = {
  big: "Big",
  small: "Small",
  mini: "Mini",
};

// ────────────────────────────────────────────

/** Convert a raw Shopify title to a clean, consistent display title */
export function formatProductTitle(raw: string): string {
  let t = raw.trim();

  // Keep "Laura Maree X NOTT ..." collabs intact — just smart-case them
  if (/^laura\s+maree/i.test(t)) {
    return smartTitleCase(t);
  }

  // Named / personalised products (e.g. "ANIEK's big BEACHY NOTT") — keep the name, clean casing
  if (/^[A-Z]+['']s\s/i.test(t)) {
    return smartTitleCase(t);
  }

  // Try to match and replace known line prefixes
  const lower = t.toLowerCase();
  for (const [key, display] of LINE_MAP) {
    if (lower.startsWith(key)) {
      const rest = t.slice(key.length).trim();
      t = display + (rest ? " " + rest : "");
      return postProcess(smartTitleCase(t));
    }
  }

  // "NOTT - COLOUR" → "Classic Colour"
  if (/^NOTT\s*[-–—]\s*/i.test(t)) {
    t = t.replace(/^NOTT\s*[-–—]\s*/i, "Classic ");
    return postProcess(smartTitleCase(t));
  }

  // Fallback: just smart-case
  return postProcess(smartTitleCase(t));
}

// ────────────────────────────────────────────

/** Post-processing: normalize "and" → "&", size suffixes */
function postProcess(title: string): string {
  let t = title;

  // Replace " And " with " & " in color portions (not at start)
  t = t.replace(/\s+And\s+/g, " & ");

  // Normalize trailing size tokens: "Chain Olive Gold Big" → "Chain Olive Gold — Big"
  const words = t.split(" ");
  const lastWord = words[words.length - 1]?.toLowerCase();
  if (words.length > 2 && SIZE_TOKENS[lastWord]) {
    words[words.length - 1] = `— ${SIZE_TOKENS[lastWord]}`;
    t = words.join(" ");
  }

  return t;
}

function smartTitleCase(str: string): string {
  return str
    .replace(/\s*\/\s*/g, " × ") // " / " → " × "
    .split(" ")
    .map((word) => {
      if (word.length === 0) return word;
      // Preserve brand tokens
      if (PRESERVE.has(word)) return word;
      // Preserve possessives like "ANIEK's"
      if (/^[A-Za-z]+'s$/i.test(word)) {
        const name = word.slice(0, -2);
        return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase() + "'s";
      }
      // Already mixed case (BigNOTT, NOTTie) — keep
      if (/[a-z]/.test(word) && /[A-Z]/.test(word.slice(1))) return word;
      // Title-case
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ")
    .replace(/\s{2,}/g, " ")
    .trim();
}
